"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildOccurrences = buildOccurrences;
exports.decideTickAction = decideTickAction;
exports.isOpenForScheduling = isOpenForScheduling;
const keys_js_1 = require("./keys.js");
const schedule_js_1 = require("./schedule.js");
const TTL_DAYS = 400;
/**
 * The occurrences that should exist for one patient on one day.
 *
 * As-needed medications produce NOTHING here. That is the load-bearing consequence of
 * not knowing ondansetron's frequency: with no occurrence there is nothing to go
 * unsatisfied, so it can never generate a missed-dose alert. A false alarm on a PRN
 * drug teaches everyone to ignore alerts, which destroys the alerts that matter.
 */
function buildOccurrences(patientId, medications, logicalDate, tz, missedDoseGraceMinutes) {
    return (0, schedule_js_1.buildDaySchedule)(medications, logicalDate, tz).map((dose) => {
        const key = (0, keys_js_1.occurrenceKey)(patientId, logicalDate, dose.medId, dose.slotId);
        const missedAtUtc = new Date(Date.parse(dose.dueAtUtc) + missedDoseGraceMinutes * 60_000).toISOString();
        return {
            ...key,
            type: 'Occurrence',
            patientId,
            logicalDate,
            medId: dose.medId,
            medVersion: dose.medVersion,
            medName: dose.medName,
            slotId: dose.slotId,
            slotLabel: dose.slotLabel,
            localTime: dose.localTime,
            quantity: dose.quantity,
            dueAtUtc: dose.dueAtUtc,
            windowEndUtc: dose.windowEndUtc,
            missedAtUtc,
            status: 'OPEN',
            remindedAt: null,
            missedAlertSentAt: null,
            satisfiedByEventId: null,
            ttl: Math.floor(Date.parse(dose.dueAtUtc) / 1000) + TTL_DAYS * 24 * 60 * 60,
            ...(0, keys_js_1.openOccurrenceIndexKeys)(dose.dueAtUtc, patientId, dose.medId, dose.slotId),
        };
    });
}
/**
 * What the scheduler should do with one open occurrence right now.
 *
 * Pure, so the whole state machine is testable without AWS. The tick applies whatever
 * this returns.
 */
function decideTickAction(occurrence, now) {
    if (occurrence.status === 'TAKEN' || occurrence.status === 'SKIPPED')
        return { kind: 'NONE' };
    if (occurrence.status === 'NOT_SCHEDULED')
        return { kind: 'NONE' };
    const nowMs = now.getTime();
    // The miss is checked FIRST. A tick that has fallen behind (a cold start, a paused
    // rule, a backlog) must not send a "time for your pill" reminder for a dose whose
    // window closed two hours ago — the useful message at that point is the missed-dose
    // alert, and a late reminder would be actively confusing.
    if (nowMs >= Date.parse(occurrence.missedAtUtc)) {
        return occurrence.missedAlertSentAt === null
            ? { kind: 'MISSED', occurrence }
            : { kind: 'NONE' };
    }
    if (nowMs >= Date.parse(occurrence.dueAtUtc) && occurrence.remindedAt === null) {
        return { kind: 'REMIND', occurrence };
    }
    return { kind: 'NONE' };
}
/** Whether an occurrence should still sit in the scheduler's work queue. */
function isOpenForScheduling(status) {
    return status === 'OPEN' || status === 'REMINDED';
}
//# sourceMappingURL=occurrences.js.map